import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
  Tooltip,
  Paper,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { Edit as EditIcon, Delete as DeleteIcon, Event as EventIcon, Hotel as HotelIcon } from '@mui/icons-material';
import { collection, query, where, getDocs, updateDoc, doc, getFirestore, Timestamp } from 'firebase/firestore';

interface Booking {
  id: string;
  guestInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  rooms: Array<{
    name: string;
    quantity: number;
  }>;
  checkIn: Timestamp;
  checkOut: Timestamp;
  totalGuests: number;
  totalAmount: number;
  status: 'pending' | 'confirmed' | 'rejected' | 'completed';
  paymentMethod: string;
  createdAt: Date;
  updatedAt: Date;
}

interface BookingManagementProps {
  selectedRestaurant: any;
}

const _timeRanges = ['Today', 'Weekly', 'Monthly', 'Yearly'];

const BookingManagement: React.FC<BookingManagementProps> = ({ selectedRestaurant }) => {
  const theme = useTheme();
  const [_timeRange, _setTimeRange] = useState('Today');
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(dayjs().add(7, 'day'));
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [_isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const fetchBookings = async () => {
    if (!selectedRestaurant?.docId) return;

    try {
      setLoading(true);
      const db = getFirestore();
      const bookingsRef = collection(db, 'room-bookings');
      
      const _startTimestamp = Timestamp.fromDate(
        startDate?.startOf('day').toDate() || new Date()
      );
      
      const endTimestamp = Timestamp.fromDate(
        endDate?.endOf('day').toDate() || new Date()
      );
      
      // Query for bookings that:
      // 1. Start within the selected range OR
      // 2. End within the selected range OR
      // 3. Span across the entire selected range
      const q = query(
        bookingsRef,
        where('restaurantId', '==', selectedRestaurant.docId),
        where('checkIn', '<=', endTimestamp) // Booking starts before or on the end date
      );

      const querySnapshot = await getDocs(q);
      const bookingsData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Booking))
        .filter(booking => {
          const bookingCheckIn = dayjs(booking.checkIn.toDate()).startOf('day');
          const bookingCheckOut = dayjs(booking.checkOut.toDate()).startOf('day');
          const selectedStart = startDate?.startOf('day');
          const selectedEnd = endDate?.endOf('day');

          // A booking overlaps if:
          // 1. Booking check-in is within the selected range OR
          // 2. Booking check-out is within the selected range OR
          // 3. Booking spans the entire selected range
          // Note: Check-out date is not counted as booked since it's available from 11 AM
          return (
            (bookingCheckIn.isSameOrAfter(selectedStart) && bookingCheckIn.isBefore(selectedEnd)) ||
            (bookingCheckOut.isAfter(selectedStart) && bookingCheckOut.isBefore(selectedEnd)) ||
            (bookingCheckIn.isBefore(selectedStart) && bookingCheckOut.isAfter(selectedStart))
          );
        });

      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchBookings uses startDate/endDate
  useEffect(() => {
    fetchBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchBookings uses startDate/endDate
  }, [selectedRestaurant?.docId, startDate, endDate]);

  const handleStatusChange = async (bookingId: string, status: string) => {
    try {
      const db = getFirestore();
      const bookingRef = doc(db, 'room-bookings', bookingId);
      await updateDoc(bookingRef, {
        status,
        updatedAt: new Date()
      });

      // Refresh bookings after status update
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setIsEditDialogOpen(true);
  };

  const handleDeleteBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedBooking) {
      // Implement delete functionality
      setIsDeleteDialogOpen(false);
      setSelectedBooking(null);
    }
  };

  const canUpdateStatus = (status: string) => {
    return status === 'pending';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: theme.palette.primary.main,
        fontWeight: 600,
        mb: 3
      }}>
        <HotelIcon /> Booking Management
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ 
            background: alpha(theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <EventIcon color="primary" />
                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
                  Select Date Range
                </Typography>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        sx={{ 
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        minDate={startDate}
                        sx={{ 
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            '&:hover fieldset': {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Total Bookings: ${bookings.length}`}
                      color="primary"
                      variant="outlined"
                      sx={{ 
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                    <Chip 
                      label={`Pending: ${bookings.filter(b => b.status === 'pending').length}`}
                      color="warning"
                      variant="outlined"
                      sx={{ 
                        borderColor: alpha(theme.palette.warning.main, 0.5),
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                    <Chip 
                      label={`Confirmed: ${bookings.filter(b => b.status === 'confirmed').length}`}
                      color="success"
                      variant="outlined"
                      sx={{ 
                        borderColor: alpha(theme.palette.success.main, 0.5),
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main, width: 150 }}>
                      Booking ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Guest Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Rooms
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Check-in
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Check-out
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Duration
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No bookings found for the selected date range
                      </TableCell>
                    </TableRow>
                  ) : (
                    bookings.map((booking) => (
                      <TableRow 
                        key={booking.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500, width: 150 }}>
                          {booking?.uoid || booking?.id}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {`${booking.guestInfo?.firstName || ''} ${booking.guestInfo?.lastName || ''}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.guestInfo?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {booking.rooms?.map((room: any) => (
                              <Chip
                                key={room.name}
                                label={`${room.name} (${room.quantity})`}
                                size="small"
                                sx={{ 
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                  '& .MuiChip-label': {
                                    px: 1,
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dayjs(booking.checkIn.toDate()).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(booking.checkIn.toDate()).format('hh:mm A')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dayjs(booking.checkOut.toDate()).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dayjs(booking.checkOut.toDate()).format('hh:mm A')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${booking?.nights} night${booking?.nights > 1 ? 's' : ''}`}
                            size="small"
                            sx={{ 
                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={booking.status} 
                            color={getStatusColor(booking.status)}
                            size="small"
                            sx={{ 
                              fontWeight: 500,
                              '& .MuiChip-label': {
                                px: 1,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            ₹{booking.totalAmount?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {canUpdateStatus(booking.status) ? (
                            <ButtonGroup size="small">
                              <Button
                                onClick={() => handleStatusChange(booking.id, 'confirmed')}
                                color="success"
                                sx={{ 
                                  minWidth: '80px',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  },
                                }}
                              >
                                Accept
                              </Button>
                              <Button
                                onClick={() => handleStatusChange(booking.id, 'rejected')}
                                color="error"
                                sx={{ 
                                  minWidth: '80px',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  },
                                }}
                              >
                                Reject
                              </Button>
                            </ButtonGroup>
                          ) : (
                            <Tooltip title="Booking status cannot be changed after being confirmed or rejected">
                              <span>
                                <ButtonGroup size="small" disabled>
                                  <Button color="success">Accept</Button>
                                  <Button color="error">Reject</Button>
                                </ButtonGroup>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleEditBooking(booking)}
                            sx={{ 
                              ml: 1,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBooking(booking)}
                            sx={{ 
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this booking? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state: any) => {
  return {
    selectedRestaurant: state.common?.selectedRestaurant
  };
};

export default connect(mapStateToProps)(BookingManagement);